/**
 * Module 6C.4 Layer 2.1 — Dashboard persistent upgrade banner.
 *
 * Rendered near the top of Dashboard for Trial and Free plans. Two CTAs:
 *   • "See plans" → navigates to /settings#billing (full comparison)
 *   • "Upgrade →" → fires the same proactive event PlanBadge uses, so
 *     the UpgradeModal pops immediately without a page nav
 *
 * Grandfathered / paid plans never see this banner (usage.plan check).
 * The banner is deliberately un-dismissible for Trial/Free — this is the
 * conversion surface for pre-launch; if a paying user later needs to
 * temporarily suppress it we can add a per-user dismiss flag then.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import {
  fetchBillingUsage, trialDaysLeft, openUpgradeProactive,
} from "@/lib/billing";


export default function UpgradeBanner() {
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await fetchBillingUsage();
      if (!cancelled) setUsage(u);
    })();
    const onChange = async () => {
      const u = await fetchBillingUsage();
      if (!cancelled) setUsage(u);
    };
    window.addEventListener("lead_studio:plan_changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("lead_studio:plan_changed", onChange);
    };
  }, []);

  if (!usage) return null;
  if (usage.plan !== "trial" && usage.plan !== "free") return null;

  const days = trialDaysLeft(usage);
  const isTrial = usage.plan === "trial";

  // Copy varies by state — trial has a countdown that gets more urgent
  // near expiry; free is a steady "you're on the free tier" reminder.
  let headline; let sub;
  if (isTrial) {
    if (days !== null && days <= 2) {
      headline = `Only ${days} day${days === 1 ? "" : "s"} left in your trial`;
      sub = "Pick a plan now to keep unlimited leads, projects, and imports.";
    } else if (days !== null && days <= 5) {
      headline = `${days} days left in your trial`;
      sub = "Choose Studio or Pro to lock in your data before the trial ends.";
    } else {
      headline = `You're on the ${usage.label} plan`;
      sub = "Upgrade any time — save ~17% with yearly billing.";
    }
  } else {
    headline = "You're on the Free plan";
    sub = `Currently limited to ${usage.leads.limit} leads/month and ${usage.projects.limit} active projects. Upgrade to unlock 1,000+ leads and bulk imports.`;
  }

  return (
    <div
      data-testid="dashboard-upgrade-banner"
      className="fade-up mb-6 lg:mb-8"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 14,
        padding: "14px 18px",
        background: "linear-gradient(135deg, #22382C 0%, #2C4A3B 100%)",
        color: "#F4EFE4",
        borderRadius: 10,
        boxShadow: "0 3px 10px rgba(34,56,44,0.15)",
      }}
    >
      <div style={{ flex: 1, minWidth: 220 }}>
        <div
          className="kicker"
          style={{
            color: "#E4C88B",
            fontSize: 10,
            letterSpacing: "0.18em",
            marginBottom: 4,
          }}
        >
          {isTrial ? "TRIAL" : "FREE PLAN"}
        </div>
        <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.35, marginBottom: 3 }}>
          {headline}
        </div>
        <div style={{ fontSize: 12, opacity: 0.78, lineHeight: 1.45 }}>{sub}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate("/settings#billing")}
          className="hover:opacity-90"
          style={{
            padding: "8px 14px", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            background: "transparent",
            color: "#F4EFE4",
            border: "1px solid rgba(244,239,228,0.3)",
            borderRadius: 6, cursor: "pointer",
          }}
          data-testid="banner-see-plans"
        >
          See plans
        </button>
        <button
          onClick={() => openUpgradeProactive({ currentPlan: usage.plan, label: usage.label })}
          className="hover:opacity-90"
          style={{
            padding: "8px 16px", fontSize: 11, fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            background: "#E4C88B",
            color: "#22382C",
            border: "1px solid #E4C88B",
            borderRadius: 6, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
          data-testid="banner-upgrade-cta"
        >
          Upgrade <ArrowRight size={13} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
